<template>
  <li
    class="exam-card"
    :class="{ 'is-completed': tab === 2, 'can-open-answer': canOpenAnswer }"
    :role="canOpenAnswer ? 'button' : null"
    :tabindex="canOpenAnswer ? 0 : null"
    :aria-label="canOpenAnswer ? `${item.exam_name}，查看答卷` : null"
    @click="openAnswerSheet"
    @keydown.enter="openAnswerSheet"
    @keydown.space.prevent="openAnswerSheet"
  >
    <div class="card-heading">
      <h2>{{ item.exam_name }}</h2>
      <span
        v-if="statusText"
        class="status mobile-status"
        :class="statusClass"
      >{{ statusText }}</span>
    </div>
    <p class="exam-time">
      <i aria-hidden="true"></i>
      <span>考试时间：{{ item.start_time | date('Y-m-d H:i') }} ~ {{ item.end_time | date('Y-m-d H:i') }}</span>
    </p>
    <div class="exam-meta">
      <span>总分：{{ item.score }}分</span>
      <span>答题时间：{{ item.exam_time }}分钟</span>
      <button
        v-if="tab === 2 && showReset"
        type="button"
        class="reset-link"
        @click.stop="$emit('reset', item)"
        @keydown.enter.stop
        @keydown.space.stop
      >重考</button>
    </div>
    <div class="card-side">
      <span
        v-if="statusText"
        class="status tablet-status"
        :class="statusClass"
      >{{ statusText }}</span>
      <div v-if="tab === 1 && showPrimary" class="card-actions">
        <button
          type="button"
          class="primary-action"
          :disabled="disabled"
          @click.stop="$emit('primary', item)"
        >{{ actionText }}</button>
      </div>
    </div>
  </li>
</template>

<script>
export default {
  name: 'MobileExamCard',
  props: {
    item: {
      type: Object,
      required: true
    },
    tab: {
      type: Number,
      required: true
    },
    actionText: {
      type: String,
      required: true
    },
    disabled: Boolean,
    showReset: Boolean,
    showPrimary: Boolean
  },
  computed: {
    canOpenAnswer() {
      return this.tab === 2 && this.showPrimary && !this.disabled
    },
    statusText() {
      if (this.tab === 2) return '已完成'
      return {
        1: '未开始',
        2: '进行中',
        3: '已结束'
      }[this.item.status] || ''
    },
    statusClass() {
      if (this.tab === 2) return 'completed'
      return {
        2: 'proceed',
        3: 'end'
      }[this.item.status] || ''
    }
  },
  methods: {
    openAnswerSheet() {
      if (this.canOpenAnswer) this.$emit('primary', this.item)
    }
  }
}
</script>

<style lang="scss" scoped>
.exam-card {
  margin-bottom: 1rem;
  padding: 1rem;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 0.375rem;

  &.can-open-answer {
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid var(--mobile-theme);
      outline-offset: 2px;
    }
  }
}

.card-heading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: space-between;

  h2 {
    display: -webkit-box;
    overflow: hidden;
    flex: 1;
    margin: 0;
    color: #222;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.5;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }
}

.status {
  flex: 0 0 auto;
  min-width: 4rem;
  padding: 0.18rem 0.45rem;
  color: var(--mobile-theme);
  font-size: 0.75rem;
  line-height: 1.35;
  text-align: center;
  background: var(--mobile-theme-light);
  border-radius: 1rem;

  &.end,
  &.completed {
    color: #93a5b3;
    background: #eef2f5;
  }
}

.tablet-status {
  display: none;
}

.exam-time {
  display: flex;
  align-items: flex-start;
  gap: 0.3rem;
  margin: 0.7rem 0 0;
  color: #666;
  font-size: 0.75rem;
  line-height: 1.45;

  i {
    flex: 0 0 auto;
    width: 0.8rem;
    height: 0.8rem;
    margin-top: 0.1rem;
    background: url('~@/assets/images/time.jpg') center / cover no-repeat;
  }
}

.exam-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.3rem 1.8rem;
  margin-top: 0.45rem;
  color: #999;
  font-size: 0.75rem;
  line-height: 1.45;
}

.is-completed .exam-meta {
  gap: 0.3rem 0.5rem;
}

.reset-link {
  position: relative;
  flex: 0 0 auto;
  height: 1.0625rem;
  padding: 0 0.35rem;
  margin-left: auto;
  color: var(--mobile-theme);
  border: 0;
  background: transparent;
  font: inherit;
  font-weight: 500;
  cursor: pointer;

  &::before {
    position: absolute;
    inset: -0.5rem;
    content: '';
  }

  &:focus-visible {
    outline: 2px solid var(--mobile-theme);
    outline-offset: 2px;
  }
}

.card-actions {
  display: flex;
  gap: 0.55rem;
  margin-top: 0.95rem;

  button {
    height: 2.5rem;
    border: 0;
    border-radius: 0.25rem;
    font: inherit;
    font-size: 0.9rem;
    cursor: pointer;
  }
}

.primary-action {
  flex: 1;
  color: #fff;
  background: var(--mobile-button-background, var(--mobile-theme));
  box-shadow: 0 0.2rem 0.5rem rgba(0, 0, 0, 0.12);

  &:disabled {
    cursor: not-allowed;
    background: #dde0e2;
    box-shadow: none;
  }
}

@media only screen and (min-width: 48rem) and (max-width: 80rem) {
  .exam-card {
    position: relative;
    display: block;
    min-height: 7rem;
    padding: 1.25rem 8rem 1.25rem 1.25rem;
    border-radius: 0.5rem;
  }

  .mobile-status {
    display: none;
  }

  .card-side {
    position: absolute;
    top: 0;
    right: 1.25rem;
    bottom: 0;
    width: 5.5rem;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    gap: 0.75rem;
  }

  .is-completed .card-side {
    top: 1.25rem;
    bottom: auto;
  }

  .tablet-status {
    display: block;
    align-self: center;
    min-width: 4.75rem;
  }

  .card-actions {
    flex-direction: column;
    margin-top: 0;
  }

  .card-heading h2 {
    font-size: 0.95rem;
    line-height: 1.4;
  }

  .status {
    min-width: 4.75rem;
    padding: 0.25rem 0.625rem;
    font-size: 0.625rem;
  }

  .exam-time {
    gap: 0.375rem;
    margin-top: 0.75rem;
    font-size: 0.625rem;

    i {
      width: 0.875rem;
      height: 0.875rem;
    }
  }

  .exam-meta {
    gap: 0.375rem 1.5rem;
    margin-top: 0.5rem;
    font-size: 0.625rem;
  }

  .card-actions button {
    height: 2.25rem;
    min-height: 2.25rem;
    font-size: 0.6875rem;
  }

  .reset-link {
    height: 1.25rem;
    font-size: 0.625rem;
  }
}
</style>
